import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  Input,
  LeafletMap,
  PageHeader,
  Pagination,
  type Column,
  type LeafletPin,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Station } from '@eon/types';
import { LayoutGrid, List, Plus, Search } from 'lucide-react';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import { StationMapPopup } from '@/components/StationMapPopup';
import { stationCoordinates, stationMapVariant } from '@/lib/station-map';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function StationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labels = usePaginationLabels();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [view, setView] = useState<'list' | 'card'>('list');

  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useMyStations({ q, page, pageSize, ...sortParams });
  const mapList = hooks.useMyStations({ page: 1, pageSize: 200 });

  const columns: Column<Station>[] = [
    {
      key: 'name',
      header: t('station.name'),
      sortable: true,
      cell: (s) => (
        <Link
          to="/stations/$stationId"
          params={{ stationId: s.id }}
          className="font-medium hover:underline"
        >
          {s.name}
        </Link>
      ),
    },
    { key: 'address', header: t('station.address'), sortable: true, cell: (s) => s.address ?? '—' },
    { key: 'city', header: t('station.detail.city'), sortable: true, cell: (s) => s.city ?? '—' },
    { key: 'devices', header: t('station.devices'), cell: (s) => s.deviceCount },
    {
      key: 'status',
      header: t('station.status'),
      cell: (s) => (
        <Badge variant={s.active ? 'success' : 'secondary'}>
          {s.active ? t('common.yes') : t('common.no')}
        </Badge>
      ),
    },
  ];

  const pins: LeafletPin[] = useMemo(() => {
    const next: LeafletPin[] = [];
    for (const s of mapList.data?.data ?? []) {
      const coords = stationCoordinates(s);
      if (!coords) continue;
      next.push({
        id: s.id,
        lat: coords.lat,
        lng: coords.lng,
        variant: stationMapVariant(s),
        popup: <StationMapPopup station={s} />,
      });
    }
    return next;
  }, [mapList.data?.data]);

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = list.data?.totalPages ?? 1;
  const stations = list.data?.data ?? [];

  return (
    <>
      <PageHeader
        title={t('nav.stations')}
        description={
          list.data
            ? t('map.description', { mapped: pins.length, total: mapList.data?.totalCount ?? totalCount })
            : undefined
        }
        actions={
          <>
            <div className="flex gap-1">
              <Button
                variant={view === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('list')}
              >
                <List className="mr-1.5 h-4 w-4" />
                {t('portal.stations.listView')}
              </Button>
              <Button
                variant={view === 'card' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('card')}
              >
                <LayoutGrid className="mr-1.5 h-4 w-4" />
                {t('portal.stations.cardView')}
              </Button>
            </div>
            <Button asChild>
              <Link to="/stations/new">
                <Plus className="mr-2 h-4 w-4" />
                {t('station.create.new')}
              </Link>
            </Button>
            <div className="relative w-full sm:w-auto">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder={t('actions.search')}
                className="w-full pl-9 sm:w-64"
              />
            </div>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          {view === 'list' ? (
            <DataTable
              data={stations}
              columns={columns}
              loading={list.isLoading}
              rowKey={(s) => s.id}
              onRowClick={(s) => navigate({ to: '/stations/$stationId', params: { stationId: s.id } })}
              sort={sort}
              onSortChange={onSortChange}
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {stations.map((s) => (
                <Card
                  key={s.id}
                  className="cursor-pointer transition-shadow hover:shadow-md"
                  onClick={() => navigate({ to: '/stations/$stationId', params: { stationId: s.id } })}
                >
                  <CardContent className="p-4">
                    <p className="font-medium">{s.name}</p>
                    <p className="text-muted-foreground text-sm">{s.address ?? '—'}</p>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <Badge variant="outline">
                        {s.deviceCount} {t('station.devices')}
                      </Badge>
                      <Badge variant={s.active ? 'success' : 'secondary'}>
                        {s.active ? t('common.yes') : t('common.no')}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {list.data && totalCount > 0 ? (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              loading={list.isFetching}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              labels={labels}
            />
          ) : null}
        </div>
        <Card>
          <CardContent className="p-2">
            <LeafletMap pins={pins} height={420} fitToPins className="overflow-hidden rounded-md" />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
